import { SyntheticEvent } from 'react';

import { clsx } from 'clsx';

import Icon from '~/src/common/components/Icon';
import ConditionalTooltip from '~/src/common/components/Tooltip';
import useClickOutside from '~/src/common/hooks/useClickOutside';
import I18n from '~/src/common/services/I18n';

import styles from './CartPickerButtons.module.scss';

interface Props {
  quantity: number;
  onQuantityChange: (currentQuantity: number, increment: number, e?: SyntheticEvent) => void;
  availableQuantity: number;
  onClickOutside: () => void;
  isOpen: boolean;
  isTooltipVisible: boolean;
  onAddQuantity?: () => void;
  onRemoveQuantity?: () => void;
  incrementStep?: number;
}

const CartPickerButtons = ({
  onQuantityChange,
  quantity,
  availableQuantity,
  onClickOutside,
  isOpen,
  onAddQuantity,
  onRemoveQuantity,
  incrementStep = 1,
}: Props) => {
  const ref = useClickOutside<HTMLDivElement>(onClickOutside, isOpen);

  const isMaxQuantityReached =
    quantity >= availableQuantity || quantity + incrementStep > availableQuantity;

  const pickerQuantity = isOpen ? quantity : '';

  return (
    <div className={styles.pickerButtonsWrapper} aria-expanded={isOpen}>
      <div className={styles.pickerButtonsContainer} ref={ref}>
        <button
          className={clsx(styles.updateQuantityButton, styles.removeQuantityButton)}
          aria-label={I18n.t('common.remove-product')}
          onClick={(e: SyntheticEvent) => {
            onRemoveQuantity?.();
            onQuantityChange(
              quantity,
              quantity - incrementStep >= 0 ? -incrementStep : -quantity,
              e,
            );
          }}
          disabled={quantity === 0}>
          <Icon name={quantity <= 1 ? 'trash' : 'minus'} size={16} color="NEUTRAL_WHITE" />
        </button>
        <span className={styles.pickerQuantity}>{pickerQuantity}</span>
        <ConditionalTooltip
          maxWidth={240}
          content={I18n.t('common.picker-sold-out-tooltip')}
          disabled={!isMaxQuantityReached || !isOpen}>
          <button
            className={clsx(styles.updateQuantityButton, styles.addQuantityButton)}
            aria-label={I18n.t('common.add-product')}
            onClick={(e: SyntheticEvent) => {
              onAddQuantity?.();
              onQuantityChange(
                quantity,
                Math.min(incrementStep, Math.max(availableQuantity - incrementStep, 0)),
                e,
              );
            }}
            disabled={isMaxQuantityReached}>
            <Icon name="plus" size={16} color="NEUTRAL_WHITE" />
          </button>
        </ConditionalTooltip>
      </div>
    </div>
  );
};

export default CartPickerButtons;
